// ============================================
// MENU PANEL COMPONENT
// Category-based menu navigation with quick add
// Supports Grid (with images) and List (compact) views
// ============================================

import React, { useState, useMemo } from 'react';
import { Search, Leaf, AlertTriangle, Star, Plus, LayoutGrid, List } from 'lucide-react';
import { cn } from '@/lib/utils';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';

export function MenuPanel({ categories, items, onItemSelect, currency }) {
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [viewMode, setViewMode] = useState('grid'); // 'grid' or 'list'

  // Filter items by category and search
  const filteredItems = useMemo(() => {
    let result = items.filter(item => item.isAvailable);
    
    if (selectedCategory !== 'all') {
      result = result.filter(item => item.category === selectedCategory);
    }
    
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      result = result.filter(item => 
        item.name.toLowerCase().includes(query) ||
        item.description?.toLowerCase().includes(query)
      );
    }
    
    return result;
  }, [items, selectedCategory, searchQuery]);

  // Group items for display
  const groupedItems = useMemo(() => {
    if (selectedCategory !== 'all') {
      return { [selectedCategory]: filteredItems };
    }
    
    return filteredItems.reduce((acc, item) => {
      const cat = item.category || 'other';
      if (!acc[cat]) acc[cat] = [];
      acc[cat].push(item);
      return acc;
    }, {});
  }, [filteredItems, selectedCategory]);

  const getPrice = (item) => {
    return item.prices?.[currency] || item.prices?.NOK || 0;
  };

  const getCategoryName = (catId) => {
    const cat = categories.find(c => c.id === catId);
    return cat?.name || catId;
  };

  return (
    <div data-testid="menu-panel" className="flex flex-col h-full bg-white rounded-lg shadow-sm overflow-hidden">
      {/* Search Bar with View Toggle */}
      <div className="p-3 border-b">
        <div className="flex items-center gap-2">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <Input
              data-testid="menu-search"
              type="text"
              placeholder="Search menu... (Ctrl+F)"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9 bg-gray-50"
            />
          </div>
          {/* View Toggle */}
          <div className="flex items-center bg-gray-100 rounded-lg p-0.5">
            <button
              data-testid="view-toggle-grid"
              onClick={() => setViewMode('grid')}
              className={cn(
                'p-2 rounded-md transition-all',
                viewMode === 'grid'
                  ? 'bg-white shadow-sm text-brand-orange'
                  : 'text-gray-500 hover:text-gray-700'
              )}
              title="Grid view with images"
            >
              <LayoutGrid className="w-4 h-4" />
            </button>
            <button
              data-testid="view-toggle-list"
              onClick={() => setViewMode('list')}
              className={cn(
                'p-2 rounded-md transition-all',
                viewMode === 'list'
                  ? 'bg-white shadow-sm text-brand-orange'
                  : 'text-gray-500 hover:text-gray-700'
              )}
              title="Compact list view"
            >
              <List className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>

      {/* Category Tabs */}
      <div className="flex items-center gap-1 p-2 border-b bg-gray-50 overflow-x-auto">
        <button
          data-testid="cat-btn-all"
          onClick={() => setSelectedCategory('all')}
          className={cn(
            'px-3 py-1.5 rounded-md text-sm font-medium whitespace-nowrap transition-all',
            selectedCategory === 'all'
              ? 'bg-brand-orange text-white'
              : 'bg-white text-gray-600 hover:bg-gray-100 border border-gray-200'
          )}
        >
          All Items
        </button>
        {categories
          .sort((a, b) => (a.sortOrder || 99) - (b.sortOrder || 99))
          .map(category => (
            <button
              key={category.id}
              data-testid={`cat-btn-${category.id}`}
              onClick={() => setSelectedCategory(category.id)}
              className={cn(
                'px-3 py-1.5 rounded-md text-sm font-medium whitespace-nowrap transition-all',
                selectedCategory === category.id
                  ? 'bg-brand-orange text-white'
                  : 'bg-white text-gray-600 hover:bg-gray-100 border border-gray-200'
              )}
            >
              {category.name}
            </button>
          ))}
      </div>

      {/* Items Grid/List */}
      <ScrollArea className="flex-1">
        <div className="p-3">
          {Object.entries(groupedItems).map(([catId, catItems]) => (
            <div key={catId} className="mb-6 last:mb-0">
              {/* Category Header (only when showing all) */}
              {selectedCategory === 'all' && (
                <h3 className="text-sm font-semibold text-gray-700 mb-3 px-1">
                  {getCategoryName(catId)}
                  <span className="text-gray-400 font-normal ml-2">
                    ({catItems.length})
                  </span>
                </h3>
              )}
              
              {/* Items - Grid or List based on viewMode */}
              {viewMode === 'grid' ? (
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-2">
                  {catItems.map(item => (
                    <MenuItemGrid
                      key={item.id}
                      item={item}
                      price={getPrice(item)}
                      currency={currency}
                      onSelect={() => onItemSelect(item)}
                    />
                  ))}
                </div>
              ) : (
                <div className="space-y-1">
                  {catItems.map(item => (
                    <MenuItemList
                      key={item.id}
                      item={item}
                      price={getPrice(item)}
                      currency={currency}
                      onSelect={() => onItemSelect(item)}
                    />
                  ))}
                </div>
              )}
            </div>
          ))}
          
          {filteredItems.length === 0 && (
            <div className="text-center py-12 text-gray-500">
              <Search className="w-12 h-12 mx-auto mb-3 opacity-30" />
              <p>No items found</p>
              {searchQuery && (
                <button 
                  onClick={() => setSearchQuery('')}
                  className="text-brand-orange hover:underline mt-2 text-sm"
                >
                  Clear search
                </button>
              )}
            </div>
          )}
        </div>
      </ScrollArea>

      {/* Footer - Quick Stats */}
      <div className="flex items-center justify-between px-4 py-2 border-t bg-gray-50 text-xs text-gray-500">
        <span>{filteredItems.length} items</span>
        <div className="flex items-center gap-3">
          <span className="flex items-center gap-1">
            <Leaf className="w-3 h-3 text-green-600" /> Vegetarian
          </span>
          <span className="flex items-center gap-1">
            <Star className="w-3 h-3 text-amber-500" /> Signature
          </span>
          <span className="text-gray-400">|</span>
          <span className="text-gray-600">
            {viewMode === 'grid' ? 'Grid' : 'List'} view
          </span>
        </div>
      </div>
    </div>
  );
}

// Grid View - Menu Item Card with Image
function MenuItemGrid({ item, price, currency, onSelect }) {
  const [imgError, setImgError] = React.useState(false);
  
  return (
    <button
      data-testid={`menu-item-${item.id}`}
      onClick={onSelect}
      className={cn(
        'group relative rounded-lg border-2 border-gray-200 bg-white overflow-hidden',
        'hover:border-brand-orange hover:shadow-md hover:-translate-y-0.5',
        'transition-all duration-150 text-left',
        'focus:outline-none focus:ring-2 focus:ring-brand-orange focus:ring-offset-1'
      )}
    >
      {/* Add indicator */}
      <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity z-10">
        <div className="bg-brand-orange rounded-full p-1">
          <Plus className="w-4 h-4 text-white" />
        </div>
      </div>

      {/* Image */}
      {item.image && !imgError ? (
        <div className="w-full h-20 bg-gray-100 overflow-hidden">
          <img 
            src={item.image} 
            alt={item.name}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-200"
            onError={() => setImgError(true)}
            loading="lazy"
          />
        </div>
      ) : (
        <div className="w-full h-20 bg-gradient-to-br from-amber-50 to-orange-50 flex items-center justify-center">
          <span className="text-3xl opacity-30">🍛</span>
        </div>
      )}
      
      {/* Content */}
      <div className="p-2">
        {/* Badges */}
        <div className="flex items-center gap-1 mb-1 flex-wrap">
          {item.isSignatureDish && (
            <Star className="w-3 h-3 text-amber-500 fill-amber-500" />
          )}
          {item.isVegetarian && (
            <Leaf className="w-3 h-3 text-green-600" />
          )}
          {item.allergens?.length > 0 && (
            <AlertTriangle className="w-3 h-3 text-amber-500" />
          )}
        </div>

        {/* Item Name */}
        <div className="font-medium text-xs text-gray-800 line-clamp-2 leading-tight mb-1">
          {item.name}
        </div>

        {/* Price */}
        <div className="font-mono font-bold text-sm text-brand-orange">
          kr {price}
        </div>
      </div>
    </button>
  );
}

// List View - Compact Menu Item Row (no images)
function MenuItemList({ item, price, currency, onSelect }) {
  return (
    <button
      data-testid={`menu-item-list-${item.id}`}
      onClick={onSelect}
      className={cn(
        'group w-full flex items-center justify-between px-3 py-2 rounded-lg',
        'bg-white border border-gray-200',
        'hover:border-brand-orange hover:bg-orange-50',
        'transition-all duration-100 text-left',
        'focus:outline-none focus:ring-2 focus:ring-brand-orange focus:ring-offset-1'
      )}
    >
      {/* Left: Name + Badges */}
      <div className="flex items-center gap-2 flex-1 min-w-0">
        {/* Badges */}
        <div className="flex items-center gap-1 flex-shrink-0">
          {item.isSignatureDish && (
            <Star className="w-3.5 h-3.5 text-amber-500 fill-amber-500" />
          )}
          {item.isVegetarian && (
            <Leaf className="w-3.5 h-3.5 text-green-600" />
          )}
        </div>
        
        {/* Item Name */}
        <span className="font-medium text-sm text-gray-800 truncate">
          {item.name}
        </span>
        
        {/* Allergen indicator */}
        {item.allergens?.length > 0 && (
          <AlertTriangle className="w-3.5 h-3.5 text-amber-500 flex-shrink-0" />
        )}
      </div>

      {/* Right: Price + Add */}
      <div className="flex items-center gap-3 flex-shrink-0">
        <span className="font-mono font-bold text-sm text-brand-orange">
          kr {price}
        </span>
        <div className="opacity-0 group-hover:opacity-100 transition-opacity">
          <Plus className="w-5 h-5 text-brand-orange" />
        </div>
      </div>
    </button>
  );
}
